import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/LanguageContext";
import BrandMark from "../components/BrandMark";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PasswordInput from "../components/PasswordInput";
import { homePathForRole } from "../lib/roleRoutes";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, acceptInvite } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localePrefilled, setLocalePrefilled] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .get(`/auth/invite/${token}`)
      .then(({ data }) => {
        setInvite(data);
        setName(data.name || "");
        // Pre-fill the language with the admin's choice on first load only.
        if (!localePrefilled && data.locale && data.locale !== locale) {
          setLocale(data.locale);
        }
        setLocalePrefilled(true);
      })
      .catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (user && typeof user === "object") {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("invite.err_password_min"));
      return;
    }
    if (password !== confirm) {
      setError(t("invite.err_password_mismatch"));
      return;
    }
    setSubmitting(true);
    const res = await acceptInvite({ token, password, name: name.trim() || undefined, locale });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    navigate(homePathForRole(res.user.role), { replace: true });
  };

  return (
    <div className="min-h-screen br-aurora flex items-center justify-center px-6 py-12 relative" data-testid="accept-invite-page">
      <div className="absolute inset-0 br-grain" />

      {/* Top-right language switcher */}
      <div className="absolute top-6 right-6 sm:top-8 sm:right-8 z-10">
        <LanguageSwitcher variant="compact" />
      </div>

      <div className="relative max-w-[520px] w-full br-fade-up">
        <div className="flex items-center gap-4 mb-10">
          <BrandMark size={48} />
          <div>
            <div className="font-serif-display italic text-[1.8rem] text-[color:var(--br-parchment)] leading-none">
              Borealis <span className="text-[color:var(--br-bronze-soft)]">Reserve</span>
            </div>
            <div className="font-mono-fin text-[0.62rem] tracking-[0.36em] uppercase text-[color:var(--br-bronze)] mt-2">
              {t("invite.brand_tagline")}
            </div>
          </div>
        </div>

        <div className="br-card br-aurora-ribbon relative px-9 py-10">
          {loadError ? (
            <div data-testid="invite-error-block">
              <div className="br-overline" style={{ color: "var(--br-red)" }}>{t("invite.error_title")}</div>
              <h1 className="font-serif-display text-[2rem] mt-4">{loadError}</h1>
              <p className="text-[0.95rem] mt-4 text-[color:var(--br-text)]">
                {t("invite.error_explainer")}
              </p>
              <p className="mt-4 text-[0.85rem] text-[color:var(--br-text-dim)]">
                {t("invite.error_assistance")}{" "}
                <a href="mailto:clients@borealisreserve.com" className="text-[color:var(--br-bronze-soft)]">
                  clients@borealisreserve.com
                </a>
              </p>
            </div>
          ) : !invite ? (
            <div className="font-mono-fin text-xs tracking-[0.32em] uppercase text-[color:var(--br-text-dim)]">
              {t("invite.checking")}
            </div>
          ) : (
            <>
              <div className="br-overline">{t("invite.overline")}</div>
              <h1 className="font-serif-display text-[2.2rem] mt-4 leading-[1.1]">
                {invite.name ? t("invite.greeting_named", { name: invite.name }) : t("invite.greeting_default")}{" "}
                <em>{t("invite.heading_em")}</em>{" "}
                {t("invite.heading_lead")}
              </h1>
              <p className="text-[0.95rem] text-[color:var(--br-text)] mt-3 leading-relaxed">
                {t("invite.intro")} <span className="text-[color:var(--br-parchment)]">{invite.email}</span>.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-6" data-testid="invite-form">
                <label className="block">
                  <span className="br-overline block mb-3">{t("invite.field_name")}</span>
                  <input
                    className="br-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="invite-name-input"
                  />
                </label>
                <label className="block">
                  <span className="br-overline block mb-3">{t("invite.field_password")}</span>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="invite-password-input"
                  />
                </label>
                <label className="block">
                  <span className="br-overline block mb-3">{t("invite.field_confirm")}</span>
                  <PasswordInput
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="invite-confirm-input"
                  />
                </label>

                {error && (
                  <div
                    className="text-[0.85rem] py-3 px-4 border"
                    style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }}
                    data-testid="invite-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="br-btn-primary w-full"
                  disabled={submitting}
                  data-testid="invite-submit-button"
                >
                  {submitting ? t("invite.cta_loading") : t("invite.cta")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
